import { cn } from '@/lib/utils/cn';
import { useSnapshotMode } from '@/lib/stores/snapshot-mode';

/**
 * SnapshotBanner — 32px top ribbon shown whenever the user is viewing a
 * frozen snapshot.
 *
 * Why this exists:
 *   - ADR-009 makes snapshots write-once. The banner is the visual
 *     contract that tells the user "this view is read-only — what you
 *     see is what was true *then*, not now." Skipping it on any
 *     snapshot-scoped screen breaks the auditability story (and our R6
 *     promise that history doesn't silently re-render).
 *
 * Behavior:
 *   - Auto-shows when `useSnapshotMode().active` is non-null. Hides (renders
 *     `null`) otherwise — safe to mount unconditionally in the app shell.
 *   - Fixed at the top of the viewport (`fixed inset-x-0 top-0 z-50`) so
 *     it sits above route content + scrolls separately. Consumers should
 *     leave 32px of top padding on the route container when a snapshot
 *     is active — the helper `useSnapshotBannerOffset()` (not in scope for
 *     this ticket) can supply that once a screen needs it.
 *   - The "Exit snapshot view" button calls `exitSnapshotMode()`. Routes
 *     that own URL-driven snapshot state (T-0101's snapshot-detail
 *     routes) should also navigate away — they own `enter`/`exit` calls
 *     in their effect cleanup; the button here is a redundant escape
 *     hatch in case the user clicks it without navigating.
 *
 * Accessibility:
 *   - `role="status"` + `aria-live="polite"` so assistive tech announces
 *     the mode change without interrupting the user. Polite (vs. assertive)
 *     because the user *initiated* the mode change by clicking into a
 *     snapshot view — it's confirmation, not an alarm.
 *
 * Tokens (README §5):
 *   - Background : `bg-ink-100`           (#E5E8EE — neutral hairline tier)
 *   - Left rail  : `bg-accent-amber-100`  (#FAEAD7 — pale amber stripe,
 *                  4px wide, matches the prototype's snapshot-banner ink
 *                  and dot accent vocabulary)
 *   - Text       : `text-ink-900`         (Avero navy)
 *   - Bottom rule: `border-ink-200`       — subtle separation from content
 */

/** Human-readable label for a {@link SnapshotKind}. Kept in this file so the
 *  banner self-contains its copy and a future translation pass touches one
 *  place. */
const KIND_LABELS = {
  pulse: 'pulse snapshot',
  readiness: 'readiness snapshot',
  health: 'health snapshot',
  manual: 'snapshot',
  client_viewer: 'client viewer snapshot',
} as const;

/**
 * Format the captured-at ISO timestamp as `YYYY-MM-DD`. The banner is a
 * compact one-liner; reading "2026-05-20" is faster than a full locale
 * date and matches the prototype's `.t-mono-sm` cadence elsewhere in the
 * product. Intentionally NOT localized — snapshot identification is a
 * cross-jurisdiction concern.
 */
function formatCapturedAt(iso: string): string {
  // Guard against malformed strings: a bad ISO falls back to the raw
  // value so the user still sees something rather than "Invalid Date".
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return iso;
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const d = String(dt.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export interface SnapshotBannerProps {
  /** Optional extra classes — kept narrow because the banner has a fixed
   *  layout contract. Useful for a future test harness or storybook frame. */
  className?: string;
}

export function SnapshotBanner({ className }: SnapshotBannerProps = {}) {
  const active = useSnapshotMode((s) => s.active);
  const exitSnapshotMode = useSnapshotMode((s) => s.exitSnapshotMode);

  if (!active) return null;

  const kindLabel = KIND_LABELS[active.kind] ?? 'snapshot';
  const capturedAt = formatCapturedAt(active.captured_at);

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        // 32px high, fixed at the top of the viewport, full width, above
        // route content. The amber left rail is a 4px inline element
        // (rendered as the first flex child) so it travels with the
        // banner even if a parent transforms or scrolls horizontally.
        'fixed inset-x-0 top-0 z-50 flex h-8 items-stretch border-b border-ink-200 bg-ink-100 text-meta text-ink-900',
        className,
      )}
      data-snapshot-kind={active.kind}
    >
      <span aria-hidden="true" className="w-1 shrink-0 bg-accent-amber-100" />
      <div className="flex flex-1 items-center justify-between gap-3 px-4">
        <span className="truncate">
          Viewing {kindLabel} captured <span className="font-mono">{capturedAt}</span> by{' '}
          <span className="font-medium">{active.captured_by}</span>
        </span>
        <button
          type="button"
          onClick={exitSnapshotMode}
          // `btn-tertiary`-equivalent inline so the banner stays self-
          // contained — no dependency on the existing Button primitive
          // whose larger sizing would burst the 32px height.
          className="shrink-0 rounded-chip px-2 py-0.5 text-meta font-medium text-ink-700 underline-offset-2 hover:text-ink-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600 focus-visible:ring-offset-1 focus-visible:ring-offset-ink-100"
        >
          Exit snapshot view
        </button>
      </div>
    </div>
  );
}

SnapshotBanner.displayName = 'SnapshotBanner';
